import { useMutation, useQuery } from "@apollo/client/react";
import {
  ActionIcon,
  Box,
  Button,
  Card,
  Collapse,
  Group,
  rem,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  UnstyledButton,
  alpha,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle,
  IconCheck,
  IconCreditCard,
  IconMinus,
  IconPackage,
  IconPlus,
  IconReceipt,
  IconTag,
} from "@tabler/icons-react";
import { graphql } from "gql.tada";
import { useCallback, useMemo, useState } from "react";
import {
  formatLimitUsage,
  getProgressColor,
  isOrganizationUsageLimit,
  useOrganizationLimits,
} from "../../../../../business/limits.hook";
import { useSelectedOrganizationId } from "../../../../../business/OrganizationId";
import { AbbrevitatedNumber } from "../../../components/AbbrevitatedNumber";
import { useConfirmModal } from "../../../components/ConfirmModal";
import { EmptyState } from "../../../components/EmptyState";
import { PageHeader } from "../../../components/PageHeader";
import { DetailPageSkeleton } from "../../../components/SkeletonLoader";
import { StatCard } from "../../../components/StatCard";
import { StatusBadge } from "../../../components/StatusBadge";

// ---------------------------------------------------------------------------
// Global type augmentation
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    endorsely_referral?: string;
  }
}

// ---------------------------------------------------------------------------
// GraphQL operations (unchanged)
// ---------------------------------------------------------------------------

const GetOrganizationStripeStatus = graphql(`
  query organization($organizationId: ID!) {
    organization(id: $organizationId) {
      stripeConfigured
    }
  }
`);

const OrganizationSubscriptionItemsUpdate = graphql(`
  mutation organizationSubscriptionItemsUpdate(
    $items: [OrganizationSubscriptionItemUpdateInput!]!
    $coupon: String
    $referral: String
  ) {
    organizationSubscriptionItemsUpdate(
      items: $items
      coupon: $coupon
      referral: $referral
    )
  }
`);

const CreatePortalSession = graphql(`
  mutation stripeCreatePortalSession {
    stripeCreatePortalSession
  }
`);

const GetSubscriptionItems = graphql(`
  query organizationCurrentSubscriptionItems {
    subscriptionItems {
      name
      description
      id
      limitsOverride {
        value
        limit
      }
      maxCount
      price
    }

    organizationCurrentSubscriptionItems {
      count
      subscriptionItemId
    }
  }
`);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles((theme) => ({
  page: {
    maxWidth: rem(1200),
  },

  // Stripe not configured warning
  warningCard: {
    backgroundColor: alpha(theme.colors.amber[9], 0.08),
    border: `1px solid ${alpha(theme.colors.amber[5], 0.3)}`,
  },

  // Package card
  packageCard: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    transition: "border-color 200ms ease, box-shadow 200ms ease",
    "&:hover": {
      borderColor: theme.colors.dark[4],
      boxShadow: "0 8px 20px rgba(0, 0, 0, 0.3)",
    },
  },
  packageCardActive: {
    borderLeft: `3px solid ${theme.colors.cyan[5]}`,
  },
  packageName: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 700,
    color: theme.colors.dark[0],
  },
  packageDescription: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
    lineHeight: 1.5,
  },
  limitRow: {
    display: "flex",
    alignItems: "center",
    gap: rem(8),
    padding: `${rem(4)} 0`,
  },
  limitValue: {
    fontWeight: 700,
    color: theme.colors.cyan[4],
    fontSize: theme.fontSizes.sm,
    whiteSpace: "nowrap" as const,
  },
  limitName: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
  },
  limitsScroll: {
    maxHeight: rem(140),
  },
  cyanIcon: {
    color: theme.colors.cyan[5],
    flexShrink: 0,
  },
  priceDisplay: {
    fontSize: rem(24),
    fontWeight: 700,
    color: theme.colors.dark[0],
    lineHeight: 1,
  },
  priceUnit: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
  lineTotal: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.dark[2],
  },

  // Quantity stepper
  stepper: {
    display: "flex",
    alignItems: "center",
    gap: rem(12),
    userSelect: "none" as const,
  },
  stepperValue: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 700,
    color: theme.colors.dark[0],
    minWidth: rem(32),
    textAlign: "center" as const,
  },

  // Order summary
  summaryBar: {
    position: "sticky" as const,
    bottom: 0,
    zIndex: 10,
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderBottom: "none",
    borderRadius: `${theme.radius.lg} ${theme.radius.lg} 0 0`,
    padding: theme.spacing.lg,
    boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.3)",
  },
  totalPrice: {
    fontSize: rem(28),
    fontWeight: 800,
    color: theme.colors.dark[0],
    lineHeight: 1,
  },
  vatNote: {
    fontSize: theme.fontSizes.xs,
    color: theme.colors.dark[3],
  },
  couponToggle: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.cyan[4],
    "&:hover": {
      textDecoration: "underline",
    },
  },

  sectionTitle: {
    fontSize: theme.fontSizes.md,
    fontWeight: 600,
    color: theme.colors.dark[1],
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
  },
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatPrice = (cents: number): string => (cents / 100).toFixed(2);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Subscription = () => {
  const { classes, cx } = useStyles();
  const organizationId = useSelectedOrganizationId();
  const { confirm, modal } = useConfirmModal();

  const [portalLoading, setPortalLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [updatedQuantities, setUpdatedQuantities] = useState<
    Record<string, number>
  >({});

  const {
    loading: stripeLoading,
    data: stripeData,
    error: stripeError,
  } = useQuery<any>(GetOrganizationStripeStatus, {
    variables: { organizationId: organizationId ?? "" },
    skip: !organizationId,
  });

  const {
    loading: itemsLoading,
    data: itemsData,
    error: itemsError,
  } = useQuery<any>(GetSubscriptionItems);

  const [stripeCreatePortalSession] = useMutation<any>(CreatePortalSession);
  const [organizationSubscriptionItemsUpdate] = useMutation<any>(
    OrganizationSubscriptionItemsUpdate,
  );

  const { limits, getLimit } = useOrganizationLimits();

  // Derived data
  const stripeConfigured = stripeData?.organization?.stripeConfigured ?? false;
  const subscriptionItems = itemsData?.subscriptionItems ?? [];
  const currentItems = itemsData?.organizationCurrentSubscriptionItems ?? [];

  const getQuantity = useCallback(
    (itemId: string): number =>
      updatedQuantities[itemId] ??
      currentItems.find((i: any) => i.subscriptionItemId === itemId)?.count ??
      0,
    [updatedQuantities, currentItems],
  );

  const setQuantity = (itemId: string, qty: number) =>
    setUpdatedQuantities((prev) => ({ ...prev, [itemId]: qty }));

  const usageStats = useMemo(
    () =>
      limits
        .filter(isOrganizationUsageLimit)
        .filter((limit) => limit.max > 0)
        .slice(0, 4),
    [limits],
  );

  // Total monthly cost
  const totalCost = useMemo(
    () =>
      subscriptionItems.reduce((acc: any, item: any) => {
        const id = String(item.id);
        const qty =
          updatedQuantities[id] ??
          currentItems.find((i: any) => i.subscriptionItemId === id)?.count ??
          0;
        return acc + (qty * item.price) / 100;
      }, 0),
    [subscriptionItems, updatedQuantities, currentItems],
  );

  // Handlers
  const handlePortalRedirect = () => {
    setPortalLoading(true);
    stripeCreatePortalSession()
      .then((res) => {
        if (res?.error) {
          throw new Error(res.error.message);
        }
        if (res.data?.stripeCreatePortalSession) {
          window.location.href = res.data.stripeCreatePortalSession;
        }
      })
      .catch(() => {
        setPortalLoading(false);
        notifications.show({
          title: "Error",
          message: "Could not open billing portal. Please try again.",
          color: "red",
          autoClose: 5000,
        });
      });
  };

  const handleUpdateSubscription = async () => {
    const confirmed = await confirm({
      title: "Update Subscription",
      message:
        "Your subscription will be updated and charged accordingly. Do you want to proceed?",
      confirmLabel: "Update",
      type: "info",
    });

    if (!confirmed) return;

    setUpdateLoading(true);
    organizationSubscriptionItemsUpdate({
      variables: {
        coupon: coupon || undefined,
        items: subscriptionItems.map((item: any) => {
          const id = String(item.id);
          return {
            subscriptionItemId: item.id,
            count: getQuantity(id),
          };
        }),
        referral: window.endorsely_referral ?? undefined,
      },
    })
      .then((data) => {
        if (data?.error) {
          throw new Error(data.error.message);
        }

        notifications.show({
          title: "Subscription updated",
          message: "Your subscription has been updated successfully!",
          color: "green",
          autoClose: 3000,
        });

        return stripeCreatePortalSession().then((res) => {
          if (res?.error) {
            throw new Error(res.error.message);
          }
          if (res.data?.stripeCreatePortalSession) {
            window.location.href = res.data.stripeCreatePortalSession;
          }
        });
      })
      .catch(() => {
        setUpdateLoading(false);
        notifications.show({
          title: "Error",
          message: "Could not update subscription. Please try again.",
          color: "red",
          autoClose: 5000,
        });
      });
  };

  // Loading state
  if (stripeLoading || itemsLoading) {
    return <DetailPageSkeleton />;
  }

  // Error state
  if (stripeError || itemsError) {
    return (
      <Stack gap="xl" className={classes.page}>
        <PageHeader
          title="Subscription"
          subtitle="Manage your plan, track usage, and adjust packages"
          showBreadcrumbs={false}
        />
        <EmptyState
          title="Failed to load subscription data"
          description="Please try refreshing the page."
          icon={<IconAlertTriangle size={48} />}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="xl" className={cx(classes.page, "wp-fade-in")}>
      {modal}

      {/* A. Page Header */}
      <PageHeader
        title="Subscription"
        subtitle="Manage your plan, track usage, and adjust packages"
        showBreadcrumbs={false}
        actions={[
          {
            label: "Manage Billing",
            icon: <IconCreditCard size={16} />,
            onClick: handlePortalRedirect,
            loading: portalLoading,
            variant: "light",
          },
        ]}
      />

      {/* E. Stripe Not Configured Warning */}
      {!stripeConfigured && (
        <Card className={classes.warningCard} p="lg" radius="lg">
          <Group gap="md">
            <ThemeIcon size={44} radius="xl" variant="light" color="amber">
              <IconAlertTriangle size={22} />
            </ThemeIcon>
            <Stack gap={4} style={{ flex: 1 }}>
              <Text fw={600} c="amber.3">
                Billing Not Configured
              </Text>
              <Text size="sm" c="dimmed">
                You need to set up your billing account before managing your
                subscription.
              </Text>
            </Stack>
            <Button
              color="amber"
              variant="filled"
              onClick={handlePortalRedirect}
              loading={portalLoading}
            >
              Set Up Billing
            </Button>
          </Group>
        </Card>
      )}

      {/* B. Usage Overview */}
      {usageStats.length > 0 && (
        <Box>
          <Text className={classes.sectionTitle} mb="md">
            Usage Overview
          </Text>
          <SimpleGrid
            cols={{ base: 1, xs: 4, md: 4 }}
            className="wp-stagger-children"
          >
            {usageStats.map((stat) => (
              <StatCard
                key={stat.id}
                label={stat.name}
                value={formatLimitUsage(stat)}
                progress={stat.pct}
                progressColor={getProgressColor(stat.pct)}
                progressLabel={`${stat.pct.toFixed(0)}% used`}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* C. Package Cards Grid */}
      <Box>
        <Text className={classes.sectionTitle} mb="md">
          Packages
        </Text>
        <SimpleGrid
          cols={{ base: 1, sm: 3, lg: 3 }}
          className="wp-stagger-children"
        >
          {subscriptionItems.map((item: any) => {
            const itemId = String(item.id);
            const qty = getQuantity(itemId);
            const isActive = qty > 0;
            const unitPrice = item.price / 100;
            const lineTotal = (unitPrice * qty).toFixed(2);
            const maxCount = item.maxCount ?? Infinity;

            return (
              <Card
                key={itemId}
                className={cx(
                  classes.packageCard,
                  isActive && classes.packageCardActive,
                  "wp-card-interactive",
                )}
                p="lg"
                radius="lg"
              >
                <Stack gap="md">
                  {/* Header */}
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Group gap="xs">
                        <IconPackage size={18} className={classes.cyanIcon} />
                        <Text className={classes.packageName}>{item.name}</Text>
                      </Group>
                      {item.description && (
                        <Text className={classes.packageDescription}>
                          {item.description}
                        </Text>
                      )}
                    </Stack>
                    {isActive && <StatusBadge status="active" size="xs" />}
                  </Group>

                  {/* What you get */}
                  {item.limitsOverride.length > 0 && (
                    <Box>
                      <Text
                        size="xs"
                        color="dimmed"
                        tt="uppercase"
                        fw={600}
                        mb={6}
                      >
                        What you get
                      </Text>
                      <ScrollArea.Autosize
                        className={classes.limitsScroll}
                        offsetScrollbars
                        scrollbarSize={4}
                      >
                        <Stack gap={2}>
                          {item.limitsOverride.map((lo: any, idx: any) => {
                            const def = getLimit(lo.limit);
                            return (
                              <div key={idx} className={classes.limitRow}>
                                <IconCheck
                                  size={14}
                                  className={classes.cyanIcon}
                                />
                                <Text className={classes.limitValue}>
                                  +
                                  <AbbrevitatedNumber value={lo.value} />
                                </Text>
                                <Text className={classes.limitName}>
                                  {def?.name ?? lo.limit}
                                </Text>
                              </div>
                            );
                          })}
                        </Stack>
                      </ScrollArea.Autosize>
                    </Box>
                  )}

                  {/* Price */}
                  <Group gap={4} align="baseline">
                    <Text className={classes.priceDisplay}>
                      {formatPrice(item.price)}
                    </Text>
                    <Text className={classes.priceUnit}>
                      &euro;/mo per unit
                    </Text>
                  </Group>

                  {/* Quantity stepper */}
                  <Box>
                    <Group justify="space-between" align="center">
                      <div className={classes.stepper}>
                        <ActionIcon
                          size="lg"
                          radius="md"
                          variant="light"
                          color="cyan"
                          disabled={qty <= 0}
                          onClick={() =>
                            setQuantity(itemId, Math.max(0, qty - 1))
                          }
                        >
                          <IconMinus size={16} />
                        </ActionIcon>
                        <Text className={classes.stepperValue}>{qty}</Text>
                        <ActionIcon
                          size="lg"
                          radius="md"
                          variant="light"
                          color="cyan"
                          disabled={qty >= maxCount}
                          onClick={() =>
                            setQuantity(itemId, Math.min(maxCount, qty + 1))
                          }
                        >
                          <IconPlus size={16} />
                        </ActionIcon>
                      </div>
                      {item.maxCount && (
                        <Text size="xs" c="dimmed">
                          max {item.maxCount}
                        </Text>
                      )}
                    </Group>
                  </Box>

                  {/* Line total */}
                  {isActive && (
                    <Group justify="space-between">
                      <Text className={classes.lineTotal}>Line total</Text>
                      <Text fw={600} c="dark.0">
                        {lineTotal}&euro;/mo
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>
      </Box>

      {/* D. Order Summary Bar */}
      <div className={classes.summaryBar}>
        <Group justify="space-between" align="center">
          <Stack gap={4}>
            <Group gap="xs" align="baseline">
              <IconReceipt size={18} className={classes.cyanIcon} />
              <Text className={classes.totalPrice}>
                {totalCost.toFixed(2)}&euro;
              </Text>
              <Text className={classes.vatNote}>/month</Text>
            </Group>
            <Text className={classes.vatNote}>VAT excluded</Text>
          </Stack>

          <Group gap="md">
            {/* Coupon toggle */}
            <Box>
              <UnstyledButton
                className={classes.couponToggle}
                onClick={() => setCouponOpen((prev) => !prev)}
              >
                <Group gap={4}>
                  <IconTag size={14} />
                  {couponOpen ? "Hide coupon" : "Have a coupon?"}
                </Group>
              </UnstyledButton>
              <Collapse in={couponOpen}>
                <TextInput
                  mt="xs"
                  placeholder="Enter coupon code"
                  value={coupon}
                  onChange={(e) => setCoupon(e.currentTarget.value)}
                  size="xs"
                  style={{ width: rem(180) }}
                />
              </Collapse>
            </Box>

            <Button
              size="md"
              className="wp-btn-press"
              onClick={handleUpdateSubscription}
              loading={updateLoading}
              leftSection={<IconCheck size={16} />}
            >
              Update Subscription
            </Button>
          </Group>
        </Group>
      </div>
    </Stack>
  );
};
